import type {
  Dispatch,
  KeyboardEvent,
} from 'react'
import {
  useEffect,
  useRef,
} from 'react'
import { keyboardKeys } from '@/constants'
import { useCloseOnQuit } from './useCloseOnQuit'

export function handleArrowKeyDown({
  focusables,
  isOpened,
  setIsOpened,
}: {
  focusables: { current: HTMLButtonElement[] }
  isOpened: boolean
  setIsOpened: Dispatch<boolean>
}) {
  return (e: KeyboardEvent<HTMLButtonElement>) => {
    const key = e.code
    const target = e.target as HTMLButtonElement
    const elements = focusables.current
    const index = elements.indexOf(target)

    // Open the menu if it is still closed
    // and when pressed key is ArrowUp of ArrowDown
    if ((!isOpened && key === keyboardKeys.DOWN) || key === keyboardKeys.UP) {
      setIsOpened(true)
    }

    // Tab key should close the menu
    if (key === keyboardKeys.TAB) {
      setIsOpened(false)
    }
    // ArrowDown key should focus next element of the menu
    // and loop back to the first focusable element
    if (key === keyboardKeys.DOWN) {
      e.preventDefault()
      const nextIndex = elements.length - 1 === index ? 0 : index + 1
      elements[nextIndex].focus()
    }
    // ArrowUp key should focus previous element of the menu
    // and loop back to the last focusable element
    if (key === keyboardKeys.UP) {
      e.preventDefault()
      const prevIndex = index === 0 ? elements.length - 1 : index - 1
      elements[prevIndex].focus()
    }
  }
}

export function useFlyoutMenu() {
  const focusablesRef = useRef<HTMLButtonElement[]>([])
  const { ref: flyoutWrapperRef, isOpened, setIsOpened } = useCloseOnQuit()

  const toggleFlyout = () => {
    setIsOpened(value => !value)
  }

  const addToRefs = (el: HTMLButtonElement) => {
    if (el && !focusablesRef.current.includes(el))
      focusablesRef.current.push(el)
  }

  useEffect(() => {
    if (!isOpened && focusablesRef.current.length > 1)
      focusablesRef.current = focusablesRef.current.slice(0, 1)
  }, [isOpened])

  return {
    flyoutWrapperRef,
    focusables: focusablesRef,
    addToRefs,
    handleArrowKeyDown,
    isOpened,
    setIsOpened,
    toggleFlyout,
  }
}
